/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Week tools — copy, templates, clear.
 *
 * The web Plan screen could add, edit and delete individual meals but had no
 * way to act on a week as a unit. Re-typing last week's plan one meal at a time
 * is the sort of chore that makes people stop using a planner.
 *
 * ── EVERY DATE IS PASSED IN ─────────────────────────────────────────────────
 * `weekStart` and `weekEnd` come from the plan response, which the server
 * computed in the household's timezone. Nothing here constructs a date from a
 * browser clock.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PlanApi } from "../../api/endpoints.ts";
import { addDays, dateRange } from "../../lib/dates.ts";
import { Modal, Pill } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";

export function WeekToolsModal({ weekStart, weekEnd, onClose }: {
  weekStart: string;
  weekEnd: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [templateName, setTemplateName] = useState("");

  const templates = useQuery({ queryKey: ["plan-templates"], queryFn: () => PlanApi.templates() });

  const refreshPlan = () => {
    void qc.invalidateQueries({ queryKey: ["plan"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const onErr = (e: Error) => toast(e.message, "danger");

  const copy = useMutation({
    mutationFn: (v: { fromStart: string; fromEnd: string; toStart: string }) => PlanApi.copy(v),
    onSuccess: (r) => {
      refreshPlan();
      toast(`${r.copied} meal${r.copied === 1 ? "" : "s"} copied`, "ok");
    },
    onError: onErr,
  });

  const saveTemplate = useMutation({
    mutationFn: () => PlanApi.saveTemplate(templateName.trim(), weekStart, weekEnd),
    onSuccess: () => {
      setTemplateName("");
      void qc.invalidateQueries({ queryKey: ["plan-templates"] });
      toast("Template saved", "ok");
    },
    onError: onErr,
  });

  const applyTemplate = useMutation({
    // overwrite: false — applying a template must never silently replace
    // something already planned.
    mutationFn: (tid: string) => PlanApi.applyTemplate(tid, weekStart, false),
    onSuccess: (r) => {
      refreshPlan();
      toast(`${r.created} added, ${r.skipped} skipped (already planned)`, "ok");
    },
    onError: onErr,
  });

  const removeTemplate = useMutation({
    mutationFn: (tid: string) => PlanApi.removeTemplate(tid),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["plan-templates"] }),
    onError: onErr,
  });

  const clear = useMutation({
    mutationFn: () => PlanApi.clear(dateRange(weekStart, weekEnd)),
    onSuccess: (r) => {
      refreshPlan();
      toast(`${r.deleted} meal${r.deleted === 1 ? "" : "s"} removed`, "ok");
    },
    onError: onErr,
  });

  return (
    <Modal title="Week tools" onClose={onClose} wide>
      <div className="space-y-5">
        <section>
          <div className="label-mono mb-2">Copy</div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost" disabled={copy.isPending}
                    onClick={() => copy.mutate({
                      fromStart: weekStart, fromEnd: weekEnd,
                      toStart: addDays(weekStart, 7),
                    })}>
              This week → next week
            </button>
            <button className="btn-ghost" disabled={copy.isPending}
                    onClick={() => copy.mutate({
                      fromStart: addDays(weekStart, -7), fromEnd: addDays(weekEnd, -7),
                      toStart: weekStart,
                    })}>
              Last week → this week
            </button>
          </div>
          <p className="mt-1.5 text-xs text-ink-3">Existing meals are never overwritten.</p>
        </section>

        <section className="border-t border-rule-soft pt-4">
          <div className="label-mono mb-2">Templates</div>
          <div className="flex gap-2">
            <input className="input" placeholder="Name this week, e.g. Busy week"
                   value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
            <button className="btn-ghost shrink-0"
                    disabled={!templateName.trim() || saveTemplate.isPending}
                    onClick={() => saveTemplate.mutate()}>Save</button>
          </div>

          {templates.data?.templates.length ? (
            <ul className="mt-3 divide-y divide-rule-soft">
              {templates.data.templates.map((tpl) => (
                <li key={tpl.id} className="flex items-center justify-between gap-2 py-2">
                  <span className="text-sm">
                    {tpl.title}
                    <Pill tone="neutral">{tpl.entry_count} meals</Pill>
                  </span>
                  <div className="flex items-center gap-2">
                    <button className="btn-ghost !px-2 !py-1 text-xs"
                            disabled={applyTemplate.isPending}
                            onClick={() => applyTemplate.mutate(tpl.id)}>Apply</button>
                    <button className="px-1.5 text-ink-3 hover:text-danger" title="Delete template"
                            onClick={() => removeTemplate.mutate(tpl.id)}>✕</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-ink-3">No templates saved yet.</p>
          )}
        </section>

        <section className="border-t border-rule-soft pt-4">
          <div className="label-mono mb-2">Clear</div>
          <button className="btn-danger" disabled={clear.isPending}
                  onClick={() => {
                    if (confirm("Remove every meal from this week?")) clear.mutate();
                  }}>
            Clear this week
          </button>
          <p className="mt-1.5 text-xs text-ink-3">
            Removes every meal from these seven days. Recipes are untouched.
          </p>
        </section>
      </div>
    </Modal>
  );
}
